export interface IWithHistory<T> {
  current: () => T;
  push: (newState: T) => T;
  redo: () => T;
  replace: (newState: T) => T;
  undo: () => T;
}
/**
 * A generic method for adding history with push/replace/undo/redo functionality to any state.
 * @param initialState T The initial state.
 * @return IWithHistory Returns an object with methods for current, push, redo, replace, and undo.
 */
export function withHistory<T>(initialState: T): IWithHistory<T> {
  let history: Array<T> = [initialState];
  let currentPoint = 0;
  return {
    /**
     * Returns the current latest history.
     * @return T Returns the current state.
     */
    current(): T {
      return history[currentPoint];
    },
    /**
     * Pushes a new state onto the undo stack.
     * @param newState The new state to push.
     * @return T Returns the new state that was pushed.
     */
    push(newState: T): T {
      if (currentPoint === history.length - 1) {
        // we're just pushing to the top of the stack
        history.push(newState);
        currentPoint++;
        return newState;
      }
      // we want to rewrite the stack from the current point upwards
      history = [...history.slice(0, currentPoint + 1), newState];
      currentPoint = history.length - 1;
      return newState;
    },
    /**
     * Pushes forward by one on the undo stack (the opposite operation of "undo").
     * If the stack cannot be forwarded, it returns the latest state.
     * @return T Returns the state at the new position of the stack.
     */
    redo(): T {
      if (currentPoint < history.length - 1) {
        return history[++currentPoint];
      }
      return history[currentPoint];
    },
    /**
     * Replaces the state at the current position without pushing a new state to the undo stack.
     * @param newState The new state that replaces the old state.
     * @return T Returns the new state that was given.
     */
    replace(newState: T): T {
      history[currentPoint] = newState;
      return newState;
    },
    /**
     * Pushes backwards by one on the undo stack. If the stack cannot be rewinded, the initial state
     * is returned.
     *
     * @return T Returns the state at the new position of the stack.
     */
    undo(): T {
      if (currentPoint < 1) {
        return history[0];
      }
      return history[--currentPoint];
    },
  };
}
